import { DRAWINGS_SAVE_DEBOUNCE_MS } from "./drawingConstants";
import { getDrawingTool } from "./drawingTools";

/**
 * Keeps the drawings of the model in the storage of the current symbol: after
 * every change with a debounce, and at once when the symbol changes, the page
 * goes away or the engine is destroyed.
 *
 * Only this module knows the symbol of the list, and it swaps both in one step,
 * so a list never reaches the key of another symbol. What is saved is compared
 * with the last known content: a selection, a switch of pairs or an unknown
 * record left out on load write nothing.
 *
 * @param {object} params
 * @param {object} params.model - createDrawingsModel
 * @param {{ read: Function, serialize: Function, write: Function }} params.storage - settings/savedDrawings.js
 */
export const createDrawingPersistence = ({ model, storage }) => {
  let symbol = null;
  let savedContent = null;
  let timer = null;

  const flush = () => {
    clearTimeout(timer);
    timer = null;

    if (symbol === null) return;

    const content = storage.serialize(model.items);

    if (content !== savedContent && storage.write(symbol, content)) savedContent = content;
  };

  const schedule = () => {
    clearTimeout(timer);
    timer = setTimeout(flush, DRAWINGS_SAVE_DEBOUNCE_MS);
  };

  const flushWhenHidden = () => {
    if (document.visibilityState === "hidden") flush();
  };

  const restore = (savedDrawings) => {
    model.clear();
    savedDrawings.forEach(({ type, points, style, isLocked, isVisible }) => {
      const item = model.add(type, points, { ...model.styleOfTool(getDrawingTool(type)), ...style });

      item.isLocked = isLocked;
      item.isVisible = isVisible;
    });
    model.changed();
  };

  const stopListening = model.subscribe(schedule);

  // pagehide: F5 and closing the tab; hidden: the only signal a mobile browser is sure to give
  window.addEventListener("pagehide", flush);
  document.addEventListener("visibilitychange", flushWhenHidden);

  return {
    /** @param {string} nextSymbol - the unsaved changes go to the old symbol, its list gives way to the saved list of this one */
    setSymbol: (nextSymbol) => {
      flush();
      symbol = nextSymbol;
      restore(storage.read(nextSymbol));
      savedContent = storage.serialize(model.items);
    },

    destroy: () => {
      flush();
      stopListening();
      window.removeEventListener("pagehide", flush);
      document.removeEventListener("visibilitychange", flushWhenHidden);
    },
  };
};
